import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQueryState, parseAsString } from 'nuqs';
import { useEdgeLinkBridge, useDeleteBridge, useEdgeLinkStatus, useSitesByBridge, generateEdgeLinkInstallCommand } from '@/lib/api/edgelink';
import { EdgeLinkStatus } from '@/lib/api/edgelink/types';
import { ConnectionType } from '@/lib/api/types.gen';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ArrowLeft,
  Cloud,
  Shield,
  Network,
  Route,
  Trash2,
  Loader2,
  Terminal,
  Server,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Share2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Time } from '@/components/ui/time';
import TunnelStatusCard from './details/TunnelStatusCard';
import BridgeRoutesTab from './details/BridgeRoutesTab';
import BridgeDeviceTab from './details/BridgeDeviceTab';
import BridgeLogsTab from './details/BridgeLogsTab';
import UninstallEdgeLinkModal from './UninstallEdgeLinkModal';
import ShareLinkDialog from './ShareLinkDialog';
import { useEdgeLinkLogs } from '@/hooks/useEdgeLinkLogs';
import { CopyButton } from '@/components/ui/copy-button';

function ConnectionTypeBadge({ type }: { type: ConnectionType }) {
  const config = {
    [ConnectionType.Cloudflared]: {
      label: 'Cloudflare Tunnel',
      icon: Cloud,
      className: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    },
    [ConnectionType.CloudflareIpsec]: {
      label: 'IPSec VPN',
      icon: Shield,
      className: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    },
  }[type] || {
    label: type,
    icon: Network,
    className: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
  };

  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.className} font-semibold text-sm`}>
      <Icon className="h-3.5 w-3.5 mr-1.5" />
      {config.label}
    </Badge>
  );
}



function ConnectionInstructionsCard({
  authToken,
  open,
  onOpenChange,
}: {
  authToken: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const resolvedInstructions = generateEdgeLinkInstallCommand(authToken);

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <Card className={cn(
        "border-lime/20 transition-all duration-300",
        open ? "bg-gradient-to-br from-lime/5 to-transparent" : "bg-zinc-900/30"
      )}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-zinc-800/30 transition-colors rounded-t-lg py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                  open ? "bg-lime/10" : "bg-zinc-800"
                )}>
                  <Terminal className={cn("h-4 w-4", open ? "text-lime" : "text-muted-foreground")} />
                </div>
                <div>
                  <CardTitle className={cn("text-sm flex items-center gap-2", !open && "text-muted-foreground")}>
                    Connection Instructions
                  </CardTitle>
                  <CardDescription className="text-xs">Run this command to connect</CardDescription>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 px-4 pb-4">
            <div className="relative flex flex-row items-center justify-between bg-zinc-950 text-zinc-100 px-4 py-2 rounded-lg text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all border border-zinc-800 leading-relaxed">
              <span className='py-2'>
                {resolvedInstructions}
              </span>
              <CopyButton textToCopy={resolvedInstructions} label="Command" />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default function EdgeLinkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: bridgeDetail, isLoading, isError } = useEdgeLinkBridge(id || '', !!id);
  const { data: statusData, isLoading: isStatusLoading } = useEdgeLinkStatus(id || '', !!id);

  // Fetch connected sites for count display
  const { data: connectedSites } = useSitesByBridge(
    bridgeDetail?.summary.id || '',
    bridgeDetail?.summary.routes || [],
    !!bridgeDetail
  );

  const deleteBridgeMutation = useDeleteBridge({
    onSuccess: () => {
      navigate('/edgelink');
    },
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uninstallDialogOpen, setUninstallDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useQueryState('tab', parseAsString.withDefault('logs'));

  // Live log streaming - always connected while on bridge page to avoid missing logs
  const { logs, isConnected, error: logsError, clearLogs } = useEdgeLinkLogs({
    bridgeId: id,
    enabled: true,
  });

  // Automatically expand instructions only if the bridge is definitely inactive (never connected)
  React.useEffect(() => {
    if (statusData?.status === EdgeLinkStatus.INACTIVE) {
      setInstructionsOpen(true);
    }
  }, [statusData?.status]);

  const handleDelete = async () => {
    if (id) {
      await deleteBridgeMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8">
        <Skeleton className="h-10 w-48 mb-8" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !bridgeDetail) {
    return (
      <div className="flex h-[calc(100vh-6rem)] flex-col items-center justify-center gap-6">
        <div className="rounded-full bg-destructive/10 p-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold text-white">Bridge not found</h1>
        <Link className={buttonVariants()} to="/edgelink">
          Back to EdgeLink
        </Link>
      </div>
    );
  }

  const bridge = bridgeDetail.summary;

  return (
    <div className="relative">
      {/* Header */}
      <div
        className={cn(
          'sticky top-14 z-20 flex items-center justify-between border-b bg-[#111] px-6 py-3'
        )}
      >
        <div className="flex items-center gap-4">
          <Link
            to="/edgelink"
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'h-10 w-10')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex h-14 w-14 flex-col items-center justify-center gap-y-1 rounded-md border border-muted/40">
            {bridge.type === ConnectionType.Cloudflared ? (
              <Cloud className="h-5 w-5 text-lime" />
            ) : (
              <Shield className="h-5 w-5 text-lime" />
            )}
            <p className="text-[10px] font-bold">BRIDGE</p>
          </div>
          <div className="flex flex-col">
            <h1 className="text-lg font-semibold">{bridge.name}</h1>
            <div className="flex items-center gap-3">
              <ConnectionTypeBadge type={bridge.type} />
              {bridge.currentClientVersion && (
                <Badge variant="outline" className="bg-lime/10 text-lime border-lime/30 font-mono text-xs">
                  v{bridge.currentClientVersion}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                Created <Time date={bridge.createdAt} format="date" noTooltip />
              </span>
              {bridge.lastSeenAt && (
                <span className="text-xs text-muted-foreground">
                  Last seen {formatDistanceToNow(new Date(bridge.lastSeenAt), { addSuffix: true })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareDialogOpen(true)}
            className="gap-2"
          >
            <Share2 className="h-4 w-4" />
            Share Status
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUninstallDialogOpen(true)}
            className="gap-2"
          >
            <Terminal className="h-4 w-4" />
            Uninstall
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete Bridge
          </Button>
        </div>
      </div>

      {/* Status Section */}
      <div className="px-6 py-4 border-b bg-[#0a0a0a]">
        <div className="space-y-3">
          <TunnelStatusCard status={statusData} isLoading={isStatusLoading} />
          <ConnectionInstructionsCard
            authToken={bridgeDetail.authToken}
            open={instructionsOpen}
            onOpenChange={setInstructionsOpen}
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
        <TabsList className="flex w-full rounded-none border-x-0 border-t-0 border-b bg-[#111] z-[30]">
          <TabsTrigger className="flex-1 px-6 data-[state=active]:bg-[#111]" value="logs">
            <Terminal className="h-4 w-4 mr-2" />
            Logs
            {isConnected && (
              <span className="ml-2 h-2 w-2 rounded-full bg-lime animate-pulse" />
            )}
          </TabsTrigger>
          <TabsTrigger className="flex-1 px-6 data-[state=active]:bg-[#111]" value="routes">
            <Route className="h-4 w-4 mr-2" />
            Routes ({bridge.routes.length})
          </TabsTrigger>
          <TabsTrigger className="flex-1 px-6 data-[state=active]:bg-[#111]" value="devices">
            <Server className="h-4 w-4 mr-2" />
            Associated Devices {connectedSites && `(${connectedSites.reduce((sum, s) => sum + s.deviceCount, 0)})`}
          </TabsTrigger>
          <TabsTrigger className="flex-1 px-6 data-[state=active]:bg-[#111]" value="config">
            <Server className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
        </TabsList>

        <div className="p-6">
          <TabsContent value="logs" className="m-0">
            <BridgeLogsTab logs={logs} onClear={clearLogs} isConnected={isConnected} error={logsError} />
          </TabsContent>

          <TabsContent value="routes" className="m-0">
            <BridgeRoutesTab routes={bridge.routes} bridgeId={bridge.id} />
          </TabsContent>

          <TabsContent value="devices" className="m-0">
            <BridgeDeviceTab bridgeId={bridge.id} routes={bridge.routes} />
          </TabsContent>

          <TabsContent value="config" className="m-0">
            <div className="grid grid-cols-2 gap-4">
              {/* Auth Token */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Auth Token</CardTitle>
                      <CardDescription>
                        The authentication token for this bridge connection
                      </CardDescription>
                    </div>
                    <CopyButton textToCopy={bridgeDetail.authToken} label="Auth Token" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-zinc-950 text-zinc-100 p-4 rounded-lg font-mono text-xs break-all border border-zinc-800">
                    {bridgeDetail.authToken}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bridge?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the EdgeLink bridge "
              {bridge.name}" and disconnect all associated routes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBridgeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Bridge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Uninstall Dialog */}
      <UninstallEdgeLinkModal
        open={uninstallDialogOpen}
        onOpenChange={setUninstallDialogOpen}
      />

      {/* Share Link Dialog */}
      <ShareLinkDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        bridgeId={bridge.id}
      />
    </div>
  );
}
